import { useState, useRef, useCallback } from 'react';
import type { AppToast } from '../context/ToastContext';
import ApiService, { handleApiErrorAsync } from '../services/api';
import { ScenarioResult, RebateDetail } from '../types';

export interface PayerAssignmentRow {
  scenario_id: number;
  payer: string;
  product_family_name: string;
  payer_percent?: number;
}

interface UseExportScenariosOptions {
  /** Optional custom filename prefix (default: 'scenario_results') */
  filenamePrefix?: string;

  /** Optional Toast ref to use for notifications (falls back to an internal ref) */
  toastRef?: React.RefObject<AppToast | null>;
}

interface UseExportScenariosReturn {
  /** Whether an export is currently in progress */
  isExporting: boolean;
  /** Passing `rebateDetails` adds a rebate breakdown sheet (Excel) or CSV (inside the ZIP). */
  handleExport: (
    format: 'csv' | 'excel',
    data: ScenarioResult[],
    rebateDetails?: RebateDetail[] | null,
    isOral?: boolean
  ) => Promise<void>;
  /** Function to trigger an export with payer assignments as a separate sheet/file */
  handleExportWithPayerAssignments: (
    format: 'csv' | 'excel',
    data: ScenarioResult[],
    payerAssignments: PayerAssignmentRow[],
    rebateDetails?: RebateDetail[] | null,
    isOral?: boolean
  ) => Promise<void>;
  /** Toast ref to attach to a Toast component */
  exportToastRef: React.RefObject<AppToast | null>;
}

/**
 * Custom hook for exporting scenario results to CSV or Excel format.
 * Provides loading state, toast notifications, and file download handling.
 */
export const useExportScenarios = (
  options: UseExportScenariosOptions = {}
): UseExportScenariosReturn => {
  const { filenamePrefix = 'scenario_results', toastRef } = options;
  const [isExporting, setIsExporting] = useState(false);
  const internalToastRef = useRef<AppToast>(null);
  const exportToastRef = toastRef ?? internalToastRef;

  const handleExport = useCallback(
    async (
      format: 'csv' | 'excel',
      data: ScenarioResult[],
      rebateDetails?: RebateDetail[] | null,
      isOral = false
    ) => {
      if (data.length === 0) {
        exportToastRef.current?.show({
          severity: 'warn',
          summary: 'No Data',
          detail: 'No results available to export',
          life: 3000,
        });
        return;
      }

      setIsExporting(true);
      try {
        const blob = await ApiService.exportScenarioResults(data, format, rebateDetails, isOral);
        // With rebate detail attached, the CSV path returns a ZIP.
        const hasRebateSheet = (rebateDetails ?? []).some(
          (d) => d.has_contract || d.rebate_source === 'Excluded'
        );
        const ext = format === 'excel' ? 'xlsx' : hasRebateSheet ? 'zip' : 'csv';
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        exportToastRef.current?.show({
          severity: 'success',
          summary: 'Export Successful',
          detail: `Results exported as ${format.toUpperCase()}`,
          life: 3000,
        });
      } catch (error) {
        console.error('Export failed:', (error as Error)?.message ?? error);
        exportToastRef.current?.show({
          severity: 'error',
          summary: 'Export Failed',
          // The real reason (server message, too large, timeout, network) instead of one
          // "log out and back in" for every failure.
          detail: await handleApiErrorAsync(error),
          life: 5000,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [filenamePrefix, exportToastRef]
  );

  const handleExportWithPayerAssignments = useCallback(
    async (
      format: 'csv' | 'excel',
      data: ScenarioResult[],
      payerAssignments: PayerAssignmentRow[],
      rebateDetails?: RebateDetail[] | null,
      isOral = false
    ) => {
      if (data.length === 0) {
        exportToastRef.current?.show({
          severity: 'warn',
          summary: 'No Data',
          detail: 'No results available to export',
          life: 3000,
        });
        return;
      }

      setIsExporting(true);
      try {
        const blob = await ApiService.exportScenarioResultsWithPayerAssignments(
          data,
          payerAssignments,
          format,
          rebateDetails,
          isOral
        );

        // For CSV with payer assignments, backend returns a ZIP
        const ext = format === 'excel' ? 'xlsx' : 'zip';
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        exportToastRef.current?.show({
          severity: 'success',
          summary: 'Export Successful',
          detail: `Results exported as ${format.toUpperCase()}${format === 'csv' ? ' (ZIP with payer assignments)' : ' (with payer assignments sheet)'}`,
          life: 3000,
        });
      } catch (error) {
        console.error('Export failed:', (error as Error)?.message ?? error);
        exportToastRef.current?.show({
          severity: 'error',
          summary: 'Export Failed',
          // The real reason (server message, too large, timeout, network) instead of one
          // "log out and back in" for every failure.
          detail: await handleApiErrorAsync(error),
          life: 5000,
        });
      } finally {
        setIsExporting(false);
      }
    },
    [filenamePrefix, exportToastRef]
  );

  return {
    isExporting,
    handleExport,
    handleExportWithPayerAssignments,
    exportToastRef,
  };
};

export default useExportScenarios;
